import React from "react";
import { ConfigProvider, App } from "antd";
import enUS from "antd/locale/en_US";
import deDE from "antd/locale/de_DE";
import { useTranslation } from "react-i18next";

/**
 * The one literal colour in the app. Everything primary-derived is generated
 * from it — the ten-step palette, hover and active states, tinted backgrounds —
 * so changing a client's brand is this line and nothing else.
 *
 * The value is antd's own default primary, which is what this product uses.
 */
const BRAND_COLOR = "#1677ff";

interface AntdConfigProviderProps {
  children: React.ReactNode;
}

export default function AntdConfigProvider({
  children,
}: AntdConfigProviderProps): React.ReactNode {
  const { i18n } = useTranslation();

  const getAntdLocale = () => {
    if (i18n.language === "de") {
      return deDE;
    }

    return enUS;
  };

  return (
    <ConfigProvider
      locale={getAntdLocale()}
      theme={{
        // The brand colour is declared once, in BRAND_COLOR above, and antd
        // derives the rest: the ten-step palette, the hover and active states,
        // the tinted backgrounds. Links are pinned to it as well, because antd
        // derives colorLink from colorInfo — a status colour that has nothing to
        // do with the brand and stays antd's blue. In the default theme the two
        // are the same value, so the difference only shows on a rebrand.
        // The font stack is left alone.
        token: {
          colorPrimary: BRAND_COLOR,
          colorLink: BRAND_COLOR,
        },
        components: {
          Menu: {
            // The inline root menu draws a 1px edge of its own
            // (activeBarBorderWidth). The sider already has an edge — the inset
            // shadow in Layout — and this one took a pixel out of the panel:
            // the menu measured 71 inside 72, so items came out 63 wide and an
            // odd width cannot centre on a whole pixel. A CSS override loses
            // here: antd's selector is three classes deep.
            activeBarBorderWidth: 0,
          },
          Layout: {
            headerHeight: "var(--header-height)",
          },
        },
      }}
    >
      <App>{children}</App>
    </ConfigProvider>
  );
}
